/* eslint-disable arrow-body-style */
import componentMap from '@/pages/dashboard/_config/compConfig';
import { useLocal } from '@/pages/dashboard/utils/customHook';
import SvgIcon from '@home/components/SvgIcon';
import React, { useCallback, useMemo, useState } from 'react';
import { Tabs, TabPanel } from 'tea-component';
import { useParams, history, useLocation } from 'umi';
import { STUDIO_OPERATION } from '@/pages/home/pages/studio/constant';
import {
  buildStudioGamePath,
  getStudioTabFromUrl,
  parseStudioUrlInfo,
} from '@/pages/home/pages/studio/utils';
import './index.scss';
import { IProps, TabItem, TParamsType } from './_type';
import classNames from 'classnames';


/**
 * 通用标签页组件，支持多标签切换、右侧插槽、国际化等功能
 * @param props 组件属性
 * @param props.tabList 标签页配置列表
 * @param props.children 子组件内容
 * @param props.defaultTab 默认选中的标签页ID
 * @returns React组件
 * 
 * 功能特性:
 * - 支持标签页图标展示
 * - 支持中英文切换
 * - 支持URL路由联动
 * - 支持自定义右侧插槽组件
 * - 支持标签页数据传递
 */
const CommonTab: React.FC<IProps> = (props) => {
  const { tabList: propsTabList = [], children, defaultTab = '', addonSlot, className, style, ...restProps } = props || {};
  const { isEN } = useLocal();
  const [rightSlotDataMap, setRightSlotDataMap] = useState<Record<string, Record<string, unknown> | undefined>>({});
  // 使用显式类型定义 useParams 的返回值
  const params = useParams() as TParamsType;
  const location = useLocation();
  const { studioId = '', gameCode = '', tab = '' } = params || {};
  const activeTab = getStudioTabFromUrl(location.pathname, location.search) || STUDIO_OPERATION;
  const { hubPath } = parseStudioUrlInfo(location.pathname);

  const resolveToggleDataKey = useCallback((): string => {
    if (typeof addonSlot?.toggleDataKey === 'string' && addonSlot.toggleDataKey) {
      return addonSlot.toggleDataKey;
    }
    return '';
  }, [addonSlot]);

  const resolveHideTabIds = useCallback((): string[] => {
    if (Array.isArray(addonSlot?.hideTabIds) && addonSlot.hideTabIds.length > 0) {
      return addonSlot.hideTabIds as string[];
    }
    if (typeof addonSlot?.hideTabId === 'string' && addonSlot.hideTabId) {
      return [addonSlot.hideTabId];
    }
    return [];
  }, [addonSlot]);

  const resolveHideSwitchOnTabIds = useCallback((): string[] => {
    if (
      Array.isArray(addonSlot?.hideSwitchOnTabIds) &&
      addonSlot.hideSwitchOnTabIds.length > 0
    ) {
      return addonSlot.hideSwitchOnTabIds as string[];
    }
    // 未配置时：默认在「开关打开会藏掉的 Tab」上也不展示开关
    return resolveHideTabIds();
  }, [addonSlot, resolveHideTabIds]);

  const tabsInfo = useMemo(() => {
    const hideTabIds = resolveHideTabIds();
    const featureEnabled = addonSlot?.enabled !== false && Boolean(addonSlot);
    const toggleDataKey = resolveToggleDataKey();
    const toggleOn = toggleDataKey
      ? Boolean(restProps?.data?.[toggleDataKey])
      : false;
    const sourceList = (propsTabList || []).filter(item => {
      if (!featureEnabled) return true;
      if (toggleOn && hideTabIds.includes(item.id)) return false;
      return true;
    });

    const tabMap: Record<string, TabItem> = {};
    const tabList = [] as string[];
    const tabs = sourceList.map(item => {
      const { id, tabName, tabNameCN, tabIcon, path } = item;
      const label = isEN ? tabName : tabNameCN;
      tabList.push(id);
      tabMap[id] = item;
      return {
        id, 
        path,
        label: (
          <div className="dbh-studio-tab-flex">
            {tabIcon && <SvgIcon type={tabIcon} className="dbh-studio-tab-icon" />}
            {label || tabName || tabNameCN}
          </div>
        ),
      };
    });
    const activeTab = tabList.includes(tab) ? tab : defaultTab;
    return { tabs, tabMap, tabList, activeTab };
  }, [
    propsTabList,
    isEN,
    tab,
    defaultTab,
    addonSlot,
    resolveHideTabIds,
    resolveToggleDataKey,
    restProps?.data,
  ]);

  const renderChildren = useMemo(() => {
    return React.Children.toArray(children).filter((child: any) =>
      child?.props?.config?.tabId === tabsInfo?.activeTab
    );
  }, [children, tabsInfo.activeTab]);

  const renderAddonSlot = useCallback(() => {
    if (!addonSlot || !componentMap[addonSlot.name]) return null;
    if (addonSlot.enabled === false) return null;
    if (resolveHideSwitchOnTabIds().includes(tabsInfo.activeTab)) return null;
    const { name, ...addonConfig } = addonSlot;
    const AddonComponent = componentMap[name];
    return React.createElement(AddonComponent, {
      ...addonConfig,
      data: restProps?.data,
      changeData: restProps?.changeData,
    });
  }, [
    addonSlot,
    resolveHideSwitchOnTabIds,
    restProps?.data,
    restProps?.changeData,
    tabsInfo.activeTab,
  ]);

  const renderRightSlot = useCallback(() => {
    const rightSlot = tabsInfo.tabMap[tabsInfo.activeTab]?.rightSlot;
    if (!rightSlot || !componentMap[rightSlot.name]) return null;

    const { name, ...resetProps } = rightSlot;
    const rightSlotComponent = componentMap[name];

    const handleRightSlotDataChange = (value: Record<string, unknown>): void => {
      setRightSlotDataMap(prev => ({
        ...prev,
        [tabsInfo.activeTab]: value,
      }));
    };

    // key 随当前 Tab 变化，强制右侧 KPIFilter 卸载重挂，与下方正文 key 配合做「切换 Tab 重置筛选」
    return React.createElement(rightSlotComponent, {
      key: tabsInfo.activeTab,
      ...resetProps,
      getData: handleRightSlotDataChange,
      data: restProps?.data || restProps,
    });
  }, [restProps, tabsInfo.activeTab, tabsInfo.tabMap]);

  const getTabPath = useCallback((targetTab: TabItem): string => {
    const targetPath = `${targetTab.path}/${targetTab.id}`.replace(/\/{2,}/g, '/');
    // gameCode 取 useParams 的值；Home 路由下为空，避免 parseStudioUrlInfo 误判 "home" 为 gameCode
    if (gameCode) {
      return buildStudioGamePath(
        studioId,
        gameCode,
        targetPath,
        activeTab,
        Boolean(hubPath),
      );
    }
    if (studioId) {
      return `/studio/${studioId}${targetPath}`.replace(/\/{2,}/g, '/');
    }
    return targetPath;
  }, [activeTab, gameCode, hubPath, studioId]);

  const handleTabChange = useCallback(
    (tab: { id: string; }) => {
      // 重复点击当前激活的 Tab 不做任何操作，避免清空当前筛选导致内容区域消失
      if (tab.id === tabsInfo.activeTab) {
        return;
      }
      const targetTab = tabsInfo.tabMap[tab.id];
      if (!targetTab) {
        return;
      }
      // 清空各 Tab 缓存的筛选结果，避免切回时仍用旧日期；新 Tab 下 KPIFilter 会重新 init 并 getData
      setRightSlotDataMap({});
      const tabPath = getTabPath(targetTab);
      const granularity = new URLSearchParams(location.search).get('granularity');
      history.push(granularity ? `${tabPath}?granularity=${granularity}` : tabPath);
    },
    [getTabPath, location.search, tabsInfo.activeTab, tabsInfo.tabMap]
  );

  const rightSlotDataVal = useMemo(() => {
    return rightSlotDataMap[tabsInfo.activeTab];
  }, [rightSlotDataMap, tabsInfo.activeTab]);

  return (
    <Tabs
      ceiling
      activeId={tabsInfo.activeTab}
      animated={false}
      tabs={tabsInfo.tabs}
      className={classNames("dbh-studio-tab", className)}
      destroyInactiveTabPanel
      onActive={handleTabChange}
      addon={(() => {
        const rightSlotNode = renderRightSlot();
        const addonSlotNode = renderAddonSlot();

        if (rightSlotNode && addonSlotNode) {
          return (
            <div className="dbh-studio-tab-addon-wrap">
              {rightSlotNode}
              {addonSlotNode}
            </div>
          );
        }

        return rightSlotNode || addonSlotNode || undefined;
      })()}
    >
      <TabPanel id={tabsInfo.activeTab} className="dbh-studio-tab-panel">
        <div className="dbh-studio-tab-panel-content">
          {React.Children.map(renderChildren, (child: any) =>
            React.cloneElement(child, {
              ...restProps,
              tabRightSlotData: rightSlotDataVal,
            })
          )}
        </div>
      </TabPanel>
    </Tabs>
  );
};

export default CommonTab;